// import {http, APIError} from './api'
import { clientConfig } from '@/config'
import { addBreadcrumb, captureException } from '@/config/sentry'
import { isDesktopRuntime, refreshDesktopSession } from '@/services/desktopAuth'
import { storePostLoginRedirect } from '@/services/postLoginRedirect'

export const API_BASE = clientConfig.apiUrl

// Token storage keys - must match AuthContext's getStorageKey()
const AUTH_TOKEN_KEY = `${clientConfig.storage.keyPrefix}${clientConfig.storage.keys.authToken}`
const REFRESH_TOKEN_KEY = `${clientConfig.storage.keyPrefix}refresh_token`

type Query = Record<string, string | number | boolean | undefined>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonBody = Record<string, any>
type HttpOptions = Omit<RequestInit, 'body'> & {
  query?: Query
  skipAuthRefresh?: boolean  // Skip token refresh for auth endpoints
  body?: BodyInit | JsonBody | null  // Plain objects auto-serialized to JSON
}

// Token refresh state to prevent concurrent refresh attempts
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

// C4: In-memory token cache (avoids localStorage reads on every request)
let _cachedAccessToken: string | null = null
let _cachedRefreshToken: string | null = null
let _memoryOnlyAuth = false

// C5: In-flight GET request deduplication map
const _inflightGets = new Map<string, Promise<unknown>>()

function mergeHeaders(base: HeadersInit, extra?: HeadersInit): HeadersInit {
  const merged = new Headers(base)
  if (extra) {
    const extraHeaders = new Headers(extra)
    extraHeaders.forEach((value, key) => merged.set(key, value))
  }
  return merged
}

export function buildAuthHeaders(extra?: HeadersInit): HeadersInit {
  const token = getAccessToken()
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined
  return mergeHeaders({ 'content-type': 'application/json', ...(authHeader || {}) }, extra)
}

/** Auth headers without Content-Type — used for FormData/multipart uploads. */
function buildAuthHeadersNoContentType(extra?: HeadersInit): HeadersInit {
  const token = getAccessToken()
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  return mergeHeaders(authHeader, extra)
}

export class APIError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
    message?: string
  ) {
    super(message || `HTTP ${status}: ${statusText}`)
    this.name = 'APIError'
  }
}

function extractErrorMessage(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined

  const envelope = payload as Record<string, unknown>
  const detail = envelope.detail ?? envelope.message ?? envelope.error
  if (typeof detail === 'string') return detail
  if (!detail || typeof detail !== 'object') return undefined

  const structured = detail as Record<string, unknown>
  const message = typeof structured.message === 'string' ? structured.message : undefined
  const preflight = structured.preflight
  const decision = preflight && typeof preflight === 'object'
    ? (preflight as Record<string, unknown>).decision
    : undefined
  const remediation = decision && typeof decision === 'object'
    ? (decision as Record<string, unknown>).remediation
    : undefined
  const recovery = typeof remediation === 'string' ? remediation : undefined

  return [message, recovery]
    .filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index)
    .join(' ') || undefined
}

function toQuery(query?: Query) {
  if (!query) return ''
  const p = new URLSearchParams()
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null) p.set(k, String(v))
  })
  const s = p.toString()
  return s ? `?${s}` : ''
}

/**
 * Get the current access token (cached in memory to avoid localStorage reads)
 */
export function getAccessToken(): string | null {
  if (_cachedAccessToken) return _cachedAccessToken
  if (_memoryOnlyAuth) return null
  if (typeof window === 'undefined') return null
  _cachedAccessToken = window.localStorage.getItem(AUTH_TOKEN_KEY)
  return _cachedAccessToken
}

/**
 * Get the current refresh token (cached in memory)
 */
export function getRefreshToken(): string | null {
  if (_cachedRefreshToken) return _cachedRefreshToken
  if (_memoryOnlyAuth) return null
  if (typeof window === 'undefined') return null
  _cachedRefreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY)
  return _cachedRefreshToken
}

/** Select the native desktop credential boundary and discard webview storage. */
export function enableMemoryOnlyTokenStorage(): void {
  _memoryOnlyAuth = true
  _cachedAccessToken = null
  _cachedRefreshToken = null
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(AUTH_TOKEN_KEY)
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** Install a short-lived native access token without making it persistent. */
export function setMemoryOnlyAccessToken(accessToken: string): void {
  _memoryOnlyAuth = true
  _cachedAccessToken = accessToken
  _cachedRefreshToken = null
}

/**
 * Store authentication tokens (updates both cache and localStorage)
 */
export function setTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return
  _cachedAccessToken = accessToken
  window.localStorage.setItem(AUTH_TOKEN_KEY, accessToken)
  if (refreshToken) {
    _cachedRefreshToken = refreshToken
    window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  } else {
    // New sessions keep the refresh credential exclusively in the backend's
    // HttpOnly cookie. Remove any value left by an older client so a stale
    // JavaScript-readable token cannot override the current cookie.
    _cachedRefreshToken = null
    window.localStorage.removeItem(REFRESH_TOKEN_KEY)
  }
}

/**
 * Clear all authentication tokens (logout)
 */
export function clearTokens(): void {
  _cachedAccessToken = null
  _cachedRefreshToken = null
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(AUTH_TOKEN_KEY)
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/**
 * Reset all module-level state for test isolation.
 * @internal — only used by tests
 */
export function _resetModuleState(): void {
  _cachedAccessToken = null
  _cachedRefreshToken = null
  _inflightGets.clear()
  isRefreshing = false
  refreshPromise = null
  _memoryOnlyAuth = false
}

/**
 * Attempt to refresh the access token using the refresh token
 * False means invalid/signed out; temporary unavailability rejects without logout.
 */
async function attemptTokenRefresh(requestHadAccessToken: boolean): Promise<boolean> {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  // The canonical refresh credential is the HttpOnly cookie set by the
  // backend. Older clients also stored a refresh token in localStorage, so
  // continue accepting that value during migration, but never require a
  // JavaScript-readable credential before attempting cookie-based refresh.
  const refreshToken = getRefreshToken()

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      if (isDesktopRuntime()) {
        // AuthProvider owns initial Keychain restoration. A signed-out/public
        // request must not start a competing native credential operation and
        // lock the user's explicit browser login out of the single-flight gate.
        // Re-check current memory state as well: an early request may have
        // captured a stale web-storage token just before AuthProvider enabled
        // Desktop's memory-only boundary and removed it.
        if (!requestHadAccessToken || !getAccessToken()) return false
        const session = await refreshDesktopSession()
        if (session?.accessToken) {
          setMemoryOnlyAccessToken(session.accessToken)
          return true
        }
        clearTokens()
        return false
      }
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        credentials: 'include', // Include cookies
      })

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearTokens()
          return false
        }
        throw new APIError(res.status, res.statusText, '',
          'Sign-in could not be refreshed right now. Please try again.')
      }

      const data = await res.json()
      if (data.success && data.data?.accessToken) {
        setTokens(data.data.accessToken, data.data.refreshToken)
        return true
      }

      throw new APIError(503, 'Service Unavailable', '',
        'Sign-in could not be refreshed right now. Please try again.')
    } catch (error) {
      // Network/provider failures are not evidence that the session was revoked.
      // Native refresh returns null for an invalid session and throws on outage.
      if (error instanceof APIError) throw error
      throw new APIError(503, 'Service Unavailable', '',
        'Sign-in could not be refreshed right now. Please try again.')
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/** Share one rotation boundary across JSON, downloads and streaming requests. */
export async function refreshAfterUnauthorized(requestAccessToken: string | null): Promise<boolean> {
  const currentAccessToken = getAccessToken()
  if (requestAccessToken && currentAccessToken && currentAccessToken !== requestAccessToken) {
    return true
  }
  return attemptTokenRefresh(Boolean(requestAccessToken))
}

/**
 * Redirect to login page (used when auth completely fails)
 */
function redirectToLogin(): void {
  if (typeof window !== 'undefined') {
    // Store current path for redirect after login
    const currentPath = window.location.pathname + window.location.search
    if (currentPath !== '/login' && currentPath !== '/signup') {
      storePostLoginRedirect(currentPath)
    }
    window.location.href = '/login'
  }
}

export async function http<T>(path: string, opts: HttpOptions = {}): Promise<T> {
  const { query } = opts
  const method = opts.method || 'GET'

  // C5: Deduplicate concurrent identical GET requests
  if (method === 'GET') {
    const dedupeKey = `${path}${toQuery(query)}`
    const inflight = _inflightGets.get(dedupeKey)
    if (inflight) return inflight as Promise<T>

    const promise = _httpInner<T>(path, opts).finally(() => {
      // Deduplicate only while the request is in flight. Retaining a settled
      // rejection makes an immediate customer Retry replay the same failure
      // without issuing a recovery request.
      _inflightGets.delete(dedupeKey)
    })
    _inflightGets.set(dedupeKey, promise)
    return promise
  }

  return _httpInner<T>(path, opts)
}

/** Download a protected binary response with the same session-refresh boundary as JSON. */
export async function httpBlob(
  path: string,
  opts: Omit<HttpOptions, 'body'> = {},
): Promise<Blob> {
  const { query, headers, skipAuthRefresh, ...rest } = opts
  const requestAccessToken = getAccessToken()
  const url = `${API_BASE}${path}${toQuery(query)}`
  const request = () => fetch(url, {
    ...rest,
    headers: buildAuthHeadersNoContentType(headers),
  })
  const send = async () => {
    try {
      return await request()
    } catch {
      throw new APIError(
        0,
        'Network Error',
        '',
        'Unable to reach the server. Please check your connection and try again.',
      )
    }
  }

  let response = await send()

  if (response.status === 401 && !skipAuthRefresh) {
    const refreshed = await refreshAfterUnauthorized(requestAccessToken)
    if (refreshed) {
      response = await send()
    }
    if (!refreshed || response.status === 401) {
      redirectToLogin()
      throw new APIError(401, 'Unauthorized', '', 'Authentication required')
    }
  }

  if (!response.ok) {
    const body = await response.text()
    let message: string | undefined
    try {
      message = extractErrorMessage(JSON.parse(body))
    } catch {
      message = body && body.length < 200 ? body : undefined
    }
    throw new APIError(response.status, response.statusText, body, message)
  }
  return response.blob()
}

async function _httpInner<T>(path: string, opts: HttpOptions = {}): Promise<T> {
  const { query, headers, skipAuthRefresh, ...rest } = opts
  const method = opts.method || 'GET'
  const startTime = performance.now()

  // Add Sentry breadcrumb for API request
  addBreadcrumb('api', `${method} ${path}`, { query }, 'info')

  try {
    // For FormData bodies, omit Content-Type so the browser sets
    // multipart/form-data with the correct boundary automatically.
    const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData
    const requestAccessToken = getAccessToken()
    const reqHeaders = isFormData
      ? buildAuthHeadersNoContentType(headers)
      : buildAuthHeaders(headers)

    // Auto-serialize plain objects to JSON (fetch doesn't do this automatically)
    const serializedBody =
      rest.body !== null &&
      rest.body !== undefined &&
      !isFormData &&
      typeof rest.body === 'object' &&
      !(rest.body instanceof ReadableStream) &&
      !(rest.body instanceof ArrayBuffer) &&
      !(rest.body instanceof Blob)
        ? JSON.stringify(rest.body)
        : rest.body

    const res = await fetch(`${API_BASE}${path}${toQuery(query)}`, {
      ...rest,
      body: serializedBody as BodyInit | null | undefined,
      headers: reqHeaders,
    })

    const text = await res.text()

    if (!res.ok) {
      // Handle 401 Unauthorized with token refresh
      if (res.status === 401 && !skipAuthRefresh) {
        // Some auth endpoints can't or shouldn't trigger refresh attempts (to avoid loops or
        // confusing UX on public flows like forgot-password).
        const nonRefreshableAuthPaths = [
          '/auth/login',
          '/auth/signup',
          '/auth/refresh',
          '/auth/google-login',
          '/auth/forgot-password',
          '/auth/reset-password',
          '/auth/verify-email',
        ]
        const isNonRefreshableAuthEndpoint = nonRefreshableAuthPaths.some((p) => path.startsWith(p))

        if (!isNonRefreshableAuthEndpoint) {
          // Another request may have completed the refresh while this
          // request's stale 401 was still in flight. In that case retry with
          // the already-rotated access token instead of rotating the HttpOnly
          // refresh cookie a second time.
          const refreshed = await refreshAfterUnauthorized(requestAccessToken)

          if (refreshed) {
            // Retry the original request with new token
            const retryRes = await fetch(`${API_BASE}${path}${toQuery(query)}`, {
              ...rest,
              body: serializedBody as BodyInit | null | undefined,
              headers: isFormData
                ? buildAuthHeadersNoContentType(headers)
                : buildAuthHeaders(headers),
            })

            const retryText = await retryRes.text()

            if (retryRes.ok) {
              return (retryText ? JSON.parse(retryText) : null) as T
            }

            // If retry still fails with 401, redirect to login
            if (retryRes.status === 401) {
              redirectToLogin()
            }

            throw new APIError(retryRes.status, retryRes.statusText, retryText)
          } else {
            // Refresh failed, redirect to login
            redirectToLogin()
          }
        }

        throw new APIError(401, 'Unauthorized', text, 'Authentication required')
      }

      // Handle 402 Payment Required (subscription expired/required)
      if (res.status === 402) {
        // Parse error body for subscription details
        let errorData: {
          message?: string
          subscription_status?: string
          trial_ended?: boolean
          action_url?: string
        } = {}
        try {
          errorData = JSON.parse(text)
        } catch {
          // Use default message if body isn't JSON
        }

        // Redirect to billing page if not already there
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname
          if (!currentPath.startsWith('/billing') && !currentPath.startsWith('/settings/billing')) {
            // Store return path for after subscription update
            window.localStorage.setItem('redirect_after_subscription', currentPath)
            window.location.href = errorData.action_url || '/billing'
            return Promise.reject(
              new APIError(402, 'Payment Required', text, errorData.message || 'Subscription required')
            )
          }
        }

        throw new APIError(
          402,
          'Payment Required',
          text,
          errorData.message || 'Please update your subscription to continue'
        )
      }

      // Parse error detail from backend JSON response
      let errorDetail: string | undefined
      try {
        const errorJson = JSON.parse(text)
        errorDetail = extractErrorMessage(errorJson)
      } catch {
        // Not JSON, use text as-is if short enough
        if (text && text.length < 200) {
          errorDetail = text
        }
      }

      // Specific error handling with user-friendly messages
      if (res.status === 400) {
        throw new APIError(400, 'Bad Request', text, errorDetail || 'Invalid request')
      }
      if (res.status === 403) {
        throw new APIError(403, 'Forbidden', text, errorDetail || 'Access denied')
      }
      if (res.status === 404) {
        throw new APIError(404, 'Not Found', text, errorDetail || 'Resource not found')
      }
      if (res.status === 429) {
        throw new APIError(429, 'Too Many Requests', text, errorDetail || 'Rate limit exceeded')
      }
      if (res.status === 500) {
        throw new APIError(500, 'Server Error', text, errorDetail || 'Internal server error')
      }
      if (res.status >= 502 && res.status <= 504) {
        throw new APIError(res.status, 'Service Unavailable', text, errorDetail || 'Server temporarily unavailable')
      }

      throw new APIError(res.status, res.statusText, text, errorDetail)
    }

    // Add success breadcrumb with timing
    const duration = Math.round(performance.now() - startTime)
    addBreadcrumb('api', `${method} ${path} - ${res.status}`, { duration, status: res.status }, 'info')

    // Handle empty body
    return (text ? JSON.parse(text) : null) as T

  } catch (error) {
    const duration = Math.round(performance.now() - startTime)

    if (error instanceof APIError) {
      // Capture 5xx errors to Sentry (server issues we need to know about)
      if (error.status >= 500) {
        captureException(error, {
          path,
          method,
          status: error.status,
          duration,
        })
      }
      // Add error breadcrumb
      addBreadcrumb('api', `${method} ${path} - ${error.status}`, { duration, status: error.status }, 'error')
      throw error
    }

    // Network error - capture to Sentry with original details, show friendly message to user
    const networkError = new APIError(
      0, 'Network Error', '',
      'Unable to reach the server. Please check your connection and try again.',
    )
    captureException(networkError, { path, method, duration, originalError: String(error) })
    addBreadcrumb('api', `${method} ${path} - Network Error`, { duration }, 'error')
    throw networkError
  }
}
